import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, CardBody, Button, Input, Select, Empty, Textarea, Badge } from "../../components/admin/UI";
import { Modal, useModal } from "../../components/admin/Modal";
import { Plus, Trash2, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { downloadXlsx } from "../../lib/download";
import { validatePhotoFile, PHOTO_ACCEPT } from "../../lib/photoValidation";
import PrivatePlayerPhoto from "../../components/PrivatePlayerPhoto";

const POSITIONS = ["Meneur", "Arrière", "Ailier", "Ailier Fort", "Pivot", "—"];

export default function Players() {
    const { activeClubId, activeSeasonId, setActiveSeasonId, isClubAdmin, roleInActiveClub } = useAuth();
    const canManagePhotos = isClubAdmin || roleInActiveClub === "coach";
    const [players, setPlayers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [filterTeam, setFilterTeam] = useState("");
    const { open, openModal, closeModal } = useModal();
    const empty = {
        first_name: "", last_name: "", dob: "", gender: "M", position: "",
        jersey_number: "", height_cm: "", weight_kg: "",
        phone: "", email: "", address: "", license_number: "", notes: "",
        team_id: "",
    };
    const [form, setForm] = useState(empty);
    const [guardian, setGuardian] = useState({});
    const [clubFields, setClubFields] = useState({});
    const [creationTeams, setCreationTeams] = useState([]);
    const [creationPhoto, setCreationPhoto] = useState(null);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [originalCompetitor, setOriginalCompetitor] = useState(false);
    const [profileHistory, setProfileHistory] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [photoState, setPhotoState] = useState({});
    const fileInputs = useRef({});

    const photoAvailable = useCallback((playerId, available) => {
        setPhotoState((current) => current[playerId]?.available === available
            ? current
            : { ...current, [playerId]: { ...current[playerId], available } });
    }, []);

    const uploadPhoto = async (playerId, file) => {
        if (!file || photoState[playerId]?.busy) return;
        const photoError = validatePhotoFile(file);
        if (photoError) { toast.error(photoError); return; }
        setPhotoState((current) => ({ ...current, [playerId]: { ...current[playerId], busy: true } }));
        const form = new FormData();
        form.append("file", file);
        try {
            await api.put(`/players/${playerId}/photo`, form, { params: { club_id: activeClubId } });
            setPhotoState((current) => ({
                ...current,
                [playerId]: { available: true, busy: false, version: (current[playerId]?.version || 0) + 1 },
            }));
            toast.success("Photo enregistrée");
        } catch (err) {
            setPhotoState((current) => ({ ...current, [playerId]: { ...current[playerId], busy: false } }));
            toast.error(formatApiError(err));
        } finally {
            if (fileInputs.current[playerId]) fileInputs.current[playerId].value = "";
        }
    };

    const removePhoto = async (playerId) => {
        if (photoState[playerId]?.busy || !window.confirm("Supprimer la photo de ce joueur ?")) return;
        setPhotoState((current) => ({ ...current, [playerId]: { ...current[playerId], busy: true } }));
        try {
            await api.delete(`/players/${playerId}/photo`, { params: { club_id: activeClubId } });
            setPhotoState((current) => ({
                ...current,
                [playerId]: { available: false, busy: false, version: (current[playerId]?.version || 0) + 1 },
            }));
            toast.success("Photo supprimée");
        } catch (err) {
            setPhotoState((current) => ({ ...current, [playerId]: { ...current[playerId], busy: false } }));
            toast.error(formatApiError(err));
        }
    };

    const exportExcel = async () => {
        setExporting(true);
        try {
            const params = { club_id: activeClubId, season_id: activeSeasonId };
            if (filterTeam) params.team_id = filterTeam;
            await downloadXlsx("/exports/players", params, "joueurs.xlsx");
        } catch (error) {
            toast.error(error.message);
        } finally {
            setExporting(false);
        }
    };

    const load = useCallback((seasonId = activeSeasonId, teamFilter = filterTeam) => {
        if (!activeClubId) return;
        const params = { club_id: activeClubId };
        if (seasonId) params.season_id = seasonId;
        api.get("/teams", { params }).then((r) => setTeams(r.data));
        if (teamFilter) params.team_id = teamFilter;
        api.get("/players", { params }).then((r) => setPlayers(r.data));
    }, [activeClubId, activeSeasonId, filterTeam]);
    useEffect(() => { load(); }, [load]);

    const openCreate = async () => {
        setEditingId(null);
        setForm({ ...empty });
        setGuardian({ first_name: "", last_name: "", relation: "tuteur", phone: "", email: "", address: "" });
        setClubFields({ school_name: "", declared_category: "", is_competitor: false });
        setCreationPhoto(null);
        setCreationTeams([]);
        try {
            const { data: seasons } = await api.get("/seasons", { params: { club_id: activeClubId } });
            const current = seasons.find((season) => season.is_active);
            if (!current) { toast.error("Aucune saison active pour ce club"); return; }
            const { data } = await api.get("/teams", { params: { club_id: activeClubId, season_id: current.id } });
            setCreationTeams(data);
        } catch (err) { toast.error(formatApiError(err)); return; }
        openModal();
    };

    const openEdit = (p) => {
        setEditingId(p.id);
        setOriginalCompetitor(p.is_competitor === true);
        setClubFields({ is_competitor: p.is_competitor === true });
        setProfileHistory((p.profile_history || []).filter((item) => item.season_id === p.season_id));
        setForm({
            first_name: p.first_name || "", last_name: p.last_name || "", dob: p.dob || "",
            gender: p.gender || "M", position: p.position || "",
            jersey_number: p.jersey_number || "", height_cm: p.height_cm || "", weight_kg: p.weight_kg || "",
            phone: p.phone || "", email: p.email || "", address: p.address || "",
            license_number: p.license_number || "", notes: p.notes || "",
            team_id: p.team_id || "",
        });
        openModal();
    };

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        const photoError = validatePhotoFile(creationPhoto);
        if (photoError) { toast.error(photoError); return; }
        if (editingId && isClubAdmin && !originalCompetitor && clubFields.is_competitor &&
            !window.confirm("Transférer ce joueur de ESCOM vers COMPETITEUR ? L'écolage passe à 12 mois. L'adhésion et les paiements existants sont conservés. Ce changement est définitif en V1.")) return;
        setSaving(true);
        const payload = { ...form };
        payload.team_id = payload.team_id || null;
        ["jersey_number", "height_cm", "weight_kg"].forEach((k) => {
            payload[k] = payload[k] === "" ? null : Number(payload[k]);
        });
        try {
            if (editingId) {
                if (isClubAdmin && clubFields.is_competitor !== originalCompetitor) payload.is_competitor = clubFields.is_competitor;
                await api.patch(`/players/${editingId}`, payload, { params: { club_id: activeClubId } });
                toast.success("Joueur mis à jour");
            } else {
                const { data: created } = await api.post("/players/admin-create", {
                    player: { ...payload, ...clubFields, email: payload.email || null,
                        school_name: clubFields.school_name || null, declared_category: clubFields.declared_category || null },
                    guardian: { ...guardian, email: guardian.email || null },
                }, { params: { club_id: activeClubId } });
                if (creationPhoto) await uploadPhoto(created.id, creationPhoto);
                setFilterTeam("");
                setActiveSeasonId(created.season_id);
                closeModal();
                load(created.season_id, "");
                toast.success("Joueur et responsable ajoutés");
                return;
            }
            closeModal();
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer ce joueur ?")) return;
        try {
            await api.delete(`/players/${id}`, { params: { club_id: activeClubId } });
            toast.success("Joueur supprimé");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const teamName = (id) => teams.find((t) => t.id === id)?.name || (id ? "—" : "Sans équipe");

    return (
        <div data-testid="players-page">
            <PageHeader
                title="Joueurs"
                subtitle="Fiches joueurs et licences"
                action={<div className="flex gap-2">
                    <Button variant="secondary" onClick={exportExcel} disabled={!activeClubId || !activeSeasonId || exporting} data-testid="export-players-button">Exporter Excel</Button>
                    {isClubAdmin && <Button onClick={openCreate} disabled={!activeClubId} data-testid="add-player-button"><Plus className="w-4 h-4" /> Nouveau joueur</Button>}
                </div>}
            />

            <div className="flex flex-wrap gap-3 mb-4">
                <Select label="Filtrer par équipe" value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)} className="w-64" data-testid="players-team-filter">
                    <option value="">Toutes les équipes</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
            </div>

            {players.length === 0 ? (
                <Empty title="Aucun joueur" hint="Ajoutez un joueur, avec ou sans équipe." />
            ) : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr className="text-left">
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">#</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Joueur</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Référence joueur</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Équipe</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Poste</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Licence</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Téléphone</th>
                                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {players.map((p) => (
                                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`player-row-${p.id}`}>
                                        <td className="px-4 py-3 font-semibold">{p.jersey_number || "—"}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <PrivatePlayerPhoto
                                                    player={p}
                                                    clubId={activeClubId}
                                                    refreshVersion={photoState[p.id]?.version || 0}
                                                    knownAbsent={photoState[p.id]?.available === false}
                                                    onAvailabilityChange={photoAvailable}
                                                />

                                                <div>
                                                    <div className="font-semibold text-slate-900">
                                                        {p.last_name?.toUpperCase()} {p.first_name}
                                                    </div>
                                                    <Badge color={p.is_competitor ? "orange" : "slate"}>{p.is_competitor ? "COMPETITEUR" : "ESCOM"}</Badge>
                                                    <div className="text-xs text-slate-500">
                                                        {p.dob || "—"} · {p.gender}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-mono whitespace-nowrap">{p.player_reference || "—"}</td>
                                        <td className="px-4 py-3"><Badge color="slate">{teamName(p.team_id)}</Badge></td>
                                        <td className="px-4 py-3 text-slate-700">{p.position || "—"}</td>
                                        <td className="px-4 py-3 text-slate-700">{p.license_number || "—"}</td>
                                        <td className="px-4 py-3 text-slate-700">{p.phone || "—"}</td>
                                        <td className="px-4 py-3 text-right">
                                            {canManagePhotos && <span className="mr-3 inline-flex items-center gap-2">
                                                <input
                                                    ref={(node) => { fileInputs.current[p.id] = node; }}
                                                    type="file"
                                                    accept={PHOTO_ACCEPT}
                                                    className="hidden"
                                                    data-testid={`photo-input-${p.id}`}
                                                    onChange={(event) => uploadPhoto(p.id, event.target.files?.[0])}
                                                />
                                                <button
                                                    type="button"
                                                    disabled={photoState[p.id]?.busy}
                                                    onClick={() => fileInputs.current[p.id]?.click()}
                                                    className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                    data-testid={`photo-upload-${p.id}`}
                                                >{photoState[p.id]?.busy ? "Chargement…" : photoState[p.id]?.available ? "Changer la photo" : "Ajouter une photo"}</button>
                                                {photoState[p.id]?.available && <button
                                                    type="button"
                                                    disabled={photoState[p.id]?.busy}
                                                    onClick={() => removePhoto(p.id)}
                                                    className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                                                    data-testid={`photo-remove-${p.id}`}
                                                >Supprimer la photo</button>}
                                            </span>}
                                            <button onClick={() => openEdit(p)} className="text-slate-500 hover:text-orange-500 mr-3" data-testid={`edit-player-${p.id}`}><Edit2 className="w-4 h-4 inline" /></button>
                                            {isClubAdmin && <button onClick={() => remove(p.id)} className="text-slate-500 hover:text-red-500" data-testid={`delete-player-${p.id}`}><Trash2 className="w-4 h-4 inline" /></button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            <Modal open={open} onClose={saving ? () => {} : closeModal} title={editingId ? "Modifier joueur" : "Nouveau joueur"} size="lg">
                <form onSubmit={submit} className="space-y-4" data-testid="player-form">
                    {editingId && <p className="text-sm" data-testid="player-reference">Référence joueur : <span className="font-mono">{players.find((p) => p.id === editingId)?.player_reference || "—"}</span></p>}
                    <p className="text-sm text-slate-500">* Champs obligatoires. Les autres champs sont facultatifs.</p>
                    <fieldset className="space-y-3">
                        <legend className="font-semibold">IDENTITÉ DU JOUEUR</legend>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Input label="Nom *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="player-last-name" />
                            <Input label="Prénom *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="player-first-name" />
                            <Input label={editingId ? "Date de naissance" : "Date de naissance *"} type="date" value={form.dob} required={!editingId} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
                            <Select label="Sexe *" value={form.gender} required onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                                <option value="M">Masculin</option><option value="F">Féminin</option>
                            </Select>
                            {!editingId && <Input label="Établissement scolaire" value={clubFields.school_name} onChange={(e) => setClubFields({ ...clubFields, school_name: e.target.value })} />}
                            <Input label="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                            <Input label="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                            {!editingId && <Input label="Photo (facultative)" type="file" accept={PHOTO_ACCEPT} onChange={(e) => {
                                const selected = e.target.files?.[0] || null;
                                const photoError = validatePhotoFile(selected);
                                if (photoError) { toast.error(photoError); e.target.value = ""; setCreationPhoto(null); }
                                else setCreationPhoto(selected);
                            }} />}
                        </div>
                    </fieldset>
                    {!editingId && <fieldset className="space-y-3">
                        <legend className="font-semibold">PARENT / RESPONSABLE</legend>
                        <p className="text-sm text-slate-500">La création manuelle ne crée pas de compte de connexion parent et n’envoie pas d’invitation.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Input label="Nom *" required value={guardian.last_name} onChange={(e) => setGuardian({ ...guardian, last_name: e.target.value })} />
                            <Input label="Prénom *" required value={guardian.first_name} onChange={(e) => setGuardian({ ...guardian, first_name: e.target.value })} />
                            <Select label="Lien avec le joueur *" required value={guardian.relation} onChange={(e) => setGuardian({ ...guardian, relation: e.target.value })}>
                                <option value="pere">Père</option><option value="mere">Mère</option><option value="tuteur">Tuteur / responsable</option>
                            </Select>
                            <Input label="Téléphone *" required value={guardian.phone} onChange={(e) => setGuardian({ ...guardian, phone: e.target.value })} />
                            <Input label="Email" type="email" value={guardian.email} onChange={(e) => setGuardian({ ...guardian, email: e.target.value })} />
                            <Input label="Adresse" value={guardian.address} onChange={(e) => setGuardian({ ...guardian, address: e.target.value })} />
                        </div>
                    </fieldset>}
                    <fieldset className="space-y-3">
                        <legend className="font-semibold">INFORMATIONS CLUB</legend>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {!editingId && <>
                                <Input label="Catégorie déclarée (facultative)" value={clubFields.declared_category} onChange={(e) => setClubFields({ ...clubFields, declared_category: e.target.value })} />
                            </>}
                            {isClubAdmin && <Select label="Profil sportif" data-testid="player-profile-select" disabled={Boolean(editingId && originalCompetitor)} value={String(clubFields.is_competitor)} onChange={(e) => setClubFields({ ...clubFields, is_competitor: e.target.value === "true" })}>
                                <option value="false">ESCOM</option><option value="true">COMPETITEUR</option>
                            </Select>}
                            <Select label="Équipe (facultative)" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} data-testid="player-team-select">
                                <option value="">Sans équipe</option>
                                {(editingId ? teams : creationTeams).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </Select>
                            <Select label="Poste" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}>
                                <option value="">—</option>{POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </Select>
                            <Input label="Numéro de maillot" type="number" value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} />
                            <Input label="Numéro de licence" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} />
                            <Input label="Taille (cm)" type="number" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} />
                            <Input label="Poids (kg)" type="number" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} />
                        </div>
                        {isClubAdmin && <p className="text-sm text-slate-600">Barème 2026–2027 : adhésion annuelle 40 000 Ar. Écolage : {clubFields.is_competitor ? "240 000 Ar sur 12 mois" : "180 000 Ar sur 9 mois"}, soit 20 000 Ar par mois. Le transfert conserve les paiements et les dispenses.</p>}
                        {editingId && profileHistory.map((item, index) => <p key={index} className="text-sm text-slate-600">{item.from_profile} → {item.to_profile} · {new Date(item.changed_at).toLocaleString("fr-FR")}</p>)}
                        <Textarea label="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </fieldset>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="secondary" type="button" disabled={saving} onClick={closeModal}>Annuler</Button>
                        <Button type="submit" disabled={saving} data-testid="player-submit">{editingId ? "Mettre à jour" : "Créer"}</Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}
